import { useState, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { matchSorter } from 'match-sorter';

/**
 * Custom scaffolder field that fetches unlinked Entra groups from backend API
 * and displays them in a dropdown.
 *
 * @see Story 1.8.1 - Filter Unlinked Entra Groups in Team Repository Template
 */

interface GroupOption {
  value: string;
  label: string;
  description: string;
  graphGroupId?: string;
}

export const UnlinkedEntraGroupPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        setError(null);

        const backendUrl = configApi.getString('backend.baseUrl');
        const response = await fetchApi.fetch(
          `${backendUrl}/api/unlinked-entra-groups/unlinked-groups`,
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch groups: ${response.statusText}`);
        }

        const data = await response.json();
        const sortedGroups = (data.groups || []).sort(
          (a: GroupOption, b: GroupOption) => a.label.localeCompare(b.label),
        );
        setGroups(sortedGroups);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch groups');
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [configApi, fetchApi]);

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    value: GroupOption | null,
  ) => {
    onChange(value?.value || '');
  };

  // Custom filter function using match-sorter for fuzzy search
  const filterOptions = (
    options: GroupOption[],
    { inputValue }: { inputValue: string },
  ) => {
    if (!inputValue) {
      return options;
    }
    return matchSorter(options, inputValue, {
      keys: ['label', 'description'],
      threshold: matchSorter.rankings.CONTAINS,
    });
  };

  // Find the selected group object from formData
  const selectedGroup = groups.find(group => group.value === formData) || null;

  // Helper text for the input field
  const getHelperText = () => {
    if (error) {
      return error;
    }
    if (!loading && groups.length > 0) {
      return "Search or select an Entra group that doesn't have a team repository yet";
    }
    return undefined;
  };

  // No options text for autocomplete
  const getNoOptionsText = () => {
    if (loading) {
      return 'Loading groups...';
    }
    if (error) {
      return error;
    }
    return 'No unlinked groups available';
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <Autocomplete
        id="unlinked-entra-group-autocomplete"
        options={groups}
        value={selectedGroup}
        onChange={handleChange}
        filterOptions={filterOptions}
        getOptionLabel={option => option.label}
        getOptionSelected={(option, value) => option.value === value.value}
        loading={loading}
        disabled={loading}
        renderInput={params => (
          <TextField
            {...params}
            label="Entra ID Group"
            required={required}
            error={rawErrors?.length > 0 && !formData}
            helperText={getHelperText()}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={option => (
          <div style={{ width: '100%' }}>
            <div style={{ fontWeight: 500 }}>{option.label}</div>
            {option.description && (
              <div style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.54)' }}>
                {option.description}
              </div>
            )}
          </div>
        )}
        noOptionsText={getNoOptionsText()}
      />
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>This field is required</FormHelperText>
      )}
    </FormControl>
  );
};
